import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  useGetApiArticlesReferencesQuery,
  useGetApiCategoriesArticleReferencesByIdQuery,
  useGetApiCategoryTreeQuery,
  usePutApiCategoryTreeSetDisabledStateMutation,
} from '../../../redux/store/api/api';
import {
  selectActiveVersion,
  selectContextArticle,
  selectContextCategory,
  selectDisableElementsDialogOpened,
  setContextArticle,
  setContextCategory,
  setDisableElementsDialogOpened,
  setFocusedTreeItem,
} from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';
import {
  useGetArticleIdsForElementIds,
  useGetCategoryIdsForElementIds,
} from '../../../hooks/useGetArticlesOrCategoriesForElementIds';
import {
  articleIdUrlParam,
  categoryIdUrlParam,
} from '../../../shared/constants';

interface IDisableElementsDialogProps {
  selectedIds: string[];
}

function DisableElementsDialog({
  selectedIds,
}: IDisableElementsDialogProps): JSX.Element {
  const { t: translation } = useTranslation();
  const dispatch = useAppDispatch();
  const dialogShow = useAppSelector(selectDisableElementsDialogOpened);
  const articles = useGetArticleIdsForElementIds(dialogShow ? selectedIds : []);
  const categories = useGetCategoryIdsForElementIds(
    dialogShow ? selectedIds : [],
  );
  const contextCategory = useAppSelector(selectContextCategory);
  const contextArticle = useAppSelector(selectContextArticle);
  const activeVersion = useAppSelector(selectActiveVersion);
  const [setDisableState, { isError, isLoading, error }] =
    usePutApiCategoryTreeSetDisabledStateMutation();
  const [searchParams] = useSearchParams();
  const activeArticleId: string = searchParams.get(articleIdUrlParam) || '';
  const activeCategoryId: string = searchParams.get(categoryIdUrlParam) || '';
  const { refetch: refetchGetCategoriesTree } = useGetApiCategoryTreeQuery();
  const { data: referencesOfArticle, refetch: refetchReferences } =
    useGetApiArticlesReferencesQuery(
      activeArticleId && activeVersion.isRecentVersion && activeVersion.id
        ? {
            articleId: activeArticleId,
            versionId: activeVersion.id,
          }
        : skipToken,
    );
  const { refetch: refetchCategoryReferences } =
    useGetApiCategoriesArticleReferencesByIdQuery(
      activeCategoryId
        ? {
            id: activeCategoryId,
          }
        : skipToken,
    );
  const referenceIdsOfActiveArticle = activeArticleId
    ? referencesOfArticle?.resultObject?.articleReferenceLists
        ?.flat()
        .flatMap((l) => l.articleReferences) ?? []
    : [];
  const categoryReferenceIdsOfActiveArticle = referenceIdsOfActiveArticle
    .map((r) => r?.targetCategoryId)
    .filter((r) => r !== undefined && r !== null);
  const articleReferenceIdsOfActiveArticle = referenceIdsOfActiveArticle
    .map((r) => r?.targetArticleId)
    .filter((r) => r !== undefined && r !== null);

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'DisableElementsError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  const handleDisableElements = () => {
    const articleIds = contextArticle.id ? [contextArticle.id] : articles;
    const categoryIds = contextCategory.id ? [contextCategory.id] : categories;
    setDisableState({
      categoryTreeBatchOperationItems: {
        articleIds,
        categoryIds,
      },
      disabled: true,
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'DisableElementsSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        dispatch(setContextCategory({}));
        dispatch(setContextArticle({}));
        dispatch(setDisableElementsDialogOpened(false));
        refetchGetCategoriesTree();
        dispatch(
          setFocusedTreeItem(
            contextCategory?.id || contextArticle?.id || selectedIds[0],
          ),
        );

        // refetch references if any ref of open article has disabled element as source or target
        if (
          activeArticleId &&
          (articleIds.includes(activeArticleId) ||
            categoryIds.some(
              (id) => categoryReferenceIdsOfActiveArticle?.includes(id),
            ) ||
            articleIds.some(
              (id) => articleReferenceIdsOfActiveArticle?.includes(id),
            ))
        ) {
          refetchReferences();
        }

        // refetch categories if category view is open
        if (activeCategoryId) {
          refetchCategoryReferences();
        }
      });
  };

  return (
    <CustomDialog
      titleId='DisableElementsDialog'
      dialogTitle={translation('disableElements')}
      show={dialogShow}
      closeFunction={() => {
        dispatch(setDisableElementsDialogOpened(false));
        dispatch(setContextCategory({}));
        dispatch(setContextArticle({}));
      }}
      actionFunction={handleDisableElements}
      closeTitle={translation('cancel')}
      actionTitle={translation('disable')}
      actionButtonDisabled={isLoading}>
      {isLoading && <Loader />}
      {!isLoading && (
        <p>
          {translation(
            contextArticle.id || contextCategory.id
              ? 'shouldDisableSelectedElement'
              : 'shouldDisableSelectedElements',
          )}
        </p>
      )}
    </CustomDialog>
  );
}

export default DisableElementsDialog;
